import { all, call, fork, put, select, takeLatest } from "redux-saga/effects";
import * as actionsCreator from "@/AppRedux/actions/paymentGateway/paymentBeam";
import * as actions from "@/AppRedux/actions/paymentGateway/paymentBeamType";
import { PaymentLinkState } from "@/AppRedux/reducers/paymentGateway/paymentBeam";
import { SagaIterator } from "redux-saga";
import { RootState } from "@/AppRedux/reducers";
import { API_HEADERS } from "@/constants/ApiSetting"
import { APIPostRequest } from "@/services/connection";
type RoomBooking = {
  room: {
    id: string;
    name: string;
    capacity: number;
    pricePerNight: number;
    images?: string[];
    description?: string;
    amenities?: string[];
  };
  qty: number;
  nights: number;
  subtotal: number;
};

type BeamOrderItem = {
  itemName: string;
  productId: string;
  sku: string;
  quantity: number;
  price: number;
  description?: string;
  imageUrl?: string;
};

function toMinorUnit(amount: number): number {
  return Math.round(amount * 100);
}

function mapRoomsToOrderItems(bookings: RoomBooking[]): BeamOrderItem[] {
  return bookings.map((item) => {
    const totalPrice = item.room.pricePerNight * item.nights;
    return {
      itemName: item.room.name,
      productId: item.room.id,
      sku: item.room.id,
      quantity: item.qty,
      price: toMinorUnit(totalPrice), // ราคาต่อห้อง * คืน → แปลงเป็นสตางค์
      ...(item.room.description && { description: item.room.description }),
      ...(item.room.images?.[0] && { imageUrl: item.room.images[0] }),
    };
  });
}

// Worker Saga: ทำงานเมื่อมี action CREATE_PAYMENT_LINK_REQUEST
function* createPaymentLinkProcess(): SagaIterator {
  try {
    // เลือกข้อมูลที่ต้องใช้จาก state
    const paymentBeamData: PaymentLinkState = yield select((state: RootState) => state.paymentBeam);
    const bookingData = yield select((state: RootState) => state.booking)
    const { cart } = bookingData
    const {
      paymentMethodTypes,
      amount,
      currency,
      referenceId,
      description,
      collectDeliveryAddress,
    } = paymentBeamData;

    const hotelProfile = yield select((state: RootState) => state.hotelProfile);
    const { propCode: prop_code } = hotelProfile;

    if (!prop_code) {
      throw new Error("Could not determine prop_code from hotelProfile state");
    }

    const redirectUrl = `${window.location.origin}/booking/${prop_code}/payment/success`;

    // สร้าง request body ให้ตรงกับโครงสร้าง Beam API
    const requestBody = {
      collectDeliveryAddress: collectDeliveryAddress,
      // หากมีการกำหนดวันหมดอายุ (expiresAt) สามารถเพิ่ม field นี้ได้ เช่น expiresAt: paymentBeamData.expiresAt,
      linkSettings: {
        card: { isEnabled: paymentMethodTypes.card },
        cardInstallments: {
          isEnabled: paymentMethodTypes.cardInstallments,
          // กำหนด installments ทุกงวดเป็น true หากเปิดใช้งานผ่อนชำระ
          installments3m: { isEnabled: paymentMethodTypes.cardInstallments },
          installments4m: { isEnabled: paymentMethodTypes.cardInstallments },
          installments6m: { isEnabled: paymentMethodTypes.cardInstallments },
          installments10m: { isEnabled: paymentMethodTypes.cardInstallments },
        },
        eWallets: { isEnabled: paymentMethodTypes.eWallets },
        mobileBanking: { isEnabled: paymentMethodTypes.mobileBanking },
        qrPromptPay: { isEnabled: paymentMethodTypes.qrPromptPay },
        buyNowPayLater: { isEnabled: paymentMethodTypes.buyNowPayLater },
      },
      order: {
        currency: currency,
        netAmount: toMinorUnit(amount),
        description: description,
        referenceId: referenceId,
        // สามารถเพิ่ม internalNote หรือ orderItems ได้หากจำเป็น

        orderItems: mapRoomsToOrderItems(cart),
      },
      redirectUrl: redirectUrl,
    };

    // เรียก Beam API เพื่อสร้าง payment link

    //const url = `https://playground.api.beamcheckout.com/api/v1/payment-links`
    const BASE_API_URL = "https://beam-api-mockup.onrender.com";
    const url = `${BASE_API_URL}/api/beam/payment-link`; // ใช้สำหรับทดสอบกับ mock server
    const API_BODY = { ...requestBody };
    //console.log("API_BODY", API_BODY);

    const response = yield call(APIPostRequest, url, API_BODY, API_HEADERS);
    
    //console.log("response ", response);
    if (response?.success === false) {
      const errorMsg = response?.data?.result_msg || "Failed to create payment link"
      yield put(actionsCreator.createPaymentLinkFailure(errorMsg))
      return
    }

    const paymentLinkId: string = response.id || ""
    const paymentLinkUrl: string = response.url || ""
    yield put(actionsCreator.createPaymentLinkSuccess(paymentLinkId, paymentLinkUrl))
  } catch (error: any) {
    // Dispatch action FAILURE พร้อม error message
    yield put(
      actionsCreator.createPaymentLinkFailure(
        error.message || "Failed to create payment link"
      )
    );
  }
}

// Watcher Saga: คอยฟัง action CREATE_PAYMENT_LINK_REQUEST
export function* createPaymentLink() {
  yield takeLatest(actions.CREATE_PAYMENT_LINK_REQUEST, createPaymentLinkProcess)
}

export default function* rootHotelProfileSaga() {
  yield all([fork(createPaymentLink)])
}
